<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="users"
      :loading="isLoadingMap.table === true"
      class="elevation-1"
    >
      <template v-slot:item="{ item }">
        <tr>
          <td class="text-center">
            <avatar
              :firstname="item.firstName"
              :lastname="item.lastName"
              :size="35"
              :font="14"
            />
          </td>
          <td>
            {{ `${item.firstName || ""} ${item.lastName || ""}`.trim() }} <br />
            <span class="caption grey--text">{{ getFancyRoleName(item.role) }}</span>
          </td>
          <td>
            {{ item.email }}
          </td>
          <td>
            {{ item.phoneNumber }}
          </td>
          <td>
            <v-chip small outlined rounded :color="userStatus(item, 'color')">
              {{ userStatus(item, "label") }}
            </v-chip>
          </td>
          <td class="text-center">
            <v-progress-circular
              v-if="isLoadingMap[item.id] === true"
              :size="20"
              :width="2"
              color="primary"
              indeterminate
            />
            <v-menu v-if="isLoadingMap[item.id] !== true" :key="item.id" left>
              <template v-slot:activator="{ on }">
                <v-btn icon v-on="on" @click="shownMenu = true">
                  <v-icon>{{ $t("generic.icon.more_vert") }}</v-icon>
                </v-btn>
              </template>
              <v-list v-show="shownMenu" dense>

                <!-- Edit User action -->
                <v-list-item @click.stop="onEdit(item)">
                  <v-list-item-content>
                    <v-list-item-title>
                      <v-icon left small>
                        {{ $t("generic.icon.edit") }}
                      </v-icon>
                      {{ $t("generic.button.edit") }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <!-- Send invite action -->
                <v-list-item
                  v-if="item.joined !== true"
                  @click.stop="onSendInvite(item.id)"
                >
                  <v-list-item-content>
                    <v-list-item-title>
                      <v-icon left small> {{ $t("generic.icon.send") }} </v-icon>
                      {{
                        item.invited
                          ? $t("user.table.resend_invite")
                          : $t("user.table.send_invite")
                      }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <!-- Deactivate User action NOT IMPLEMENTED -->
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>
                      <v-icon left small>
                        {{ $t("generic.icon.not_interested") }}
                      </v-icon>
                      {{ $t("generic.button.deactivate") }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <!-- Delete User action -->
                <user-delete-dialog @action="onDelete" :id="item.id">
                  <template v-slot:activator="{ on }">
                    <v-list-item v-on="on" @click.stop="">
                      <v-list-item-content>
                        <v-list-item-title class="error--text">
                          <v-icon left small color="error">
                            {{ $t("generic.icon.delete") }}
                          </v-icon>
                          {{ $t("generic.button.delete") }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </user-delete-dialog>
              </v-list>
            </v-menu>
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import Avatar from "@/components/Avatar";
import UserDeleteDialog from "./UserDeleteDialog";

export default {
  components: { UserDeleteDialog, Avatar },
  data() {
    return {
      shownMenu: true,
      isLoadingMap: {},
      users: [],
      headers: [
        {
          text: "",
        },
        {
          text: this.$t('generic.table_headers.name'),
        },
        {
          text: this.$t('generic.table_headers.email'),
        },
        {
          text: this.$t('generic.table_headers.phone'),
        },
        {
          text: this.$t('generic.table_headers.status'),
        },
        {
          text: this.$t('generic.table_headers.actions'),
          align: "right",
        },
      ],
    };
  },
  computed: {
    ...mapGetters(['allRoles', 'undeletedUsers']),
    items() {
      return this.undeletedUsers;
    },
  },
  async mounted() {
    await this.loadTable();
  },
  methods: {
    ...mapActions(["fetchTenantUsers", "inviteTenantUser"]),
    onClose() {
      this.shownMenu = false
    },
    onEdit(item) {
      this.$router.push({
        name: 'update-user',
        params: { id: item.id, model: item, action: 'E' },
      });
    },
    async onDelete() {
      const successMsg = this.$t('generic.message.success', [this.$t('entity.user'), this.$t('generic.action_performed.deleted')])
      const errorMsg = this.$t('generic.message.error', [this.$t('entity.user'), this.$t('generic.action_performed.deleted')])

      await this.loadTable()
        .then(() => {
          this.showSnackbar(successMsg, this.$t('generic.status.success'))
          this.loadTable();
        }).catch(() => {
          this.showSnackbar(errorMsg, this.$t('generic.status.error'));
        }).finally(() => { this.setIsLoadingMap("table", false) })
    },
    getFancyRoleName(role) {
      return this.allRoles.filter(r => r.value === role)[0].label
    },
    userStatus(user, key) {
      const map = {
        JOINED: {
          color: "green",
          label: this.$t('generic.status.joined'),
        },
        INVITED: {
          color: "warning",
          label: this.$t('generic.status.invited'),
        },
        NOT_INVITED: {
          color: "error",
          label: this.$t('generic.status.not_invited'),
        },
      };

      if (user.joined === true) {
        return map.JOINED[key];
      }
      if (user.invited === true) {
        return map.INVITED[key];
      }

      return map.NOT_INVITED[key];
    },
    setIsLoadingMap(id, value) {
      this.isLoadingMap = {
        ...this.isLoadingMap,
        [id]: !!value,
      };
    },
    async onSendInvite(uid) {
      this.setIsLoadingMap(uid, true);
      const payload = { uid };
      await this.inviteTenantUser(payload)
        .then(() => {
          this.loadTable();
        })
        .finally(() => {
          this.setIsLoadingMap(uid, false);
        });
    },
    async loadTable() {
      this.setIsLoadingMap("table", true);

      const users = await this.fetchTenantUsers()
        .then((res) => res.data)
        .finally(() => {
          this.setIsLoadingMap("table", false);
        });

      this.users = [...users];
    },
  },
};
</script>
